import { InnerCargo, CarModel, StockStatus, PriceTerms } from 'models/StockModel';
import { ApiBase } from 'utils/api';
import { createQueryString, forceDownload, objectToFormData } from 'utils/common';

export type FindParams = {
    status?: string,
    dates?: {
        from: string,
        to: string
    },
    voyage?: number,
    yard?: number,
    limit?: number,
    page?: number,
    filter?: any,
    parties?: number[],
    payer?: string,
}

export default class CarApi {
    private base: ApiBase;

    constructor(base: ApiBase) {
        this.base = base;
    }

    get = (id: number) => this.base.axios
        .get(`/api/cars/${id}`)
        .then(res => res.data.data);

    getMultiple = (ids: number[]) => this.base.axios
        .get(`/api/cars/multiple`, {params: {ids}})
        .then(res => res.data);

    find = (params: FindParams) => this.base.axios
        .get(`/api/cars`, {params})
        .then(this.convertYard);

    voyage = (id: number | string, full?: boolean) => this.base.axios
        .get(`/api/cars/voyage/${id}`, {params: {full}})
        .then(this.convertYard);

    port = (code: string) => this.base.axios
        .get(`/api/cars/port/${code}`)
        .then(res => res.data);

    convertYard = (res: any) => {
        const main = res.data;
        main.data = main.data.map((car: CarModel) => ({...car, yard: car.yards.find(y => !y.dateOut)}))
        return main;
    };

    create = (data: any) => this.base.axios
        .post(`/api/cars`, data);

    createStep2 = (id: number, data: { make: string, model: string, makeAlt: string, modelAlt: string }) => this.base.axios
        .post(`/api/cars/${id}/model`, data);

    update = (car: CarModel) => this.base.axios
        .patch(`/api/cars/${car.id}`, car);

    remove = (carId: number) => this.base.axios
        .delete(`/api/cars/${carId}`);

    storeExpCertData = (id: number, data: { referenceNum?: string, validUntil: string, }) => this.base.axios
        .post(`/api/cars/${id}/export-certificate/data`, data);

    getDocument = (id: number, type: string) => this.base.axios
        .get(`/api/cars/${id}/doc/${type}`)
        .then(res => res.data);

    downloadDocument = (id: number, type: string) => this.base.axios
        .get(`/api/cars/${id}/doc/${type}/download`, {responseType: 'blob'})
        .then(forceDownload);

    uploadDocument = (id: number, type: string, file: File) => this.base.axios
        .post(`/api/cars/${id}/doc/${type}`,
            objectToFormData({file}),
            {headers: {'Content-Type': 'multipart/form-data'}}
        );

    addExpCertTrackCode = (data: any) => this.base.axios
        .put(`/api/cars/export-certificate/track`, data);

    changeChassis = (id: number, chassis: string) => this.base.axios
        .put(`/api/cars/${id}/chassis`, {chassis});

    setTallyNumber = (id: number, number: number) => this.base.axios
        .put(`/api/cars/${id}/tally-number`, {number});

    setJpInvBatch = (id: number, batch: number) => this.base.axios
        .put(`/api/cars/${id}/jp-inv`, {batch});

    setJpInvPos = (id: number, pos: number) => this.base.axios
        .put(`/api/cars/${id}/jp-inv`, {pos});

    setStatus = (id: number, status: StockStatus) => this.base.axios
        .put(`/api/cars/${id}/status`, {status});

    setPriceTerms = (id: number, terms: PriceTerms) => this.base.axios
        .put(`/api/cars/${id}/price-terms`, {terms});

    moveToYard = (carIds: number[], yardId: number) => this.base.axios
        .put(`/api/cars/yard/${yardId}`, {carIds});

    book = (car_ids: number[], voyageId: number) => this.base.axios
        .post(`/api/booking/${voyageId}`, {car_ids});

    cancelBooking = (car_ids: number[], reason: string) => this.base.axios
        .post(`/api/booking?_method=DELETE`, {car_ids, reason});

    setFreightPrices = (prices: any) => this.base.axios
        .put(`/api/cars/freight-price`, {prices});

    cancel = (id: number, reason: string) => this.base.axios
        .post(`/api/cars/${id}/cancel`, {reason});

    batchCreate = (data: { file: File, cols: { [k: string]: number } }) => this.base.axios
        .post('/api/cars/batch/create',
            objectToFormData(data),
            {headers: {'Content-Type': 'multipart/form-data'}}
        );

    batchUpdate = (cars: Record<number, any>) => this.base.axios
        .post('/api/cars/batch/update', {cars});

    batchYardIn = (data: { file: File, cols: { [k: string]: number }, yardId: number }) => this.base.axios
        .post('/api/cars/batch/yard-in',
            objectToFormData(data),
            {headers: {'Content-Type': 'multipart/form-data'}}
        );

    batchUploadEC = (files: File[]) => {
        const formData = new FormData();

        files.forEach(file => {
            formData.append('files[]', file)
        });

        return this.base.axios.post('/api/cars/batch/ec',
            formData,
            {headers: {'Content-Type': 'multipart/form-data'}});
    };

    batchUploadECMultiPage = (data: { companyId: number, data: any, file: File }) => this.base.axios
        .post('/api/cars/batch/ec-mp',
            objectToFormData(data),
            {headers: {'Content-Type': 'multipart/form-data'}});

    batchUploadPhotos = (file: File) => this.base.axios
        .post('/api/cars/batch/photos',
            objectToFormData({file}),
            {headers: {'Content-Type': 'multipart/form-data'}});

    yardIn = (data: { dates: { [k: number]: string }, yardId: number }) => this.base.axios
        .post('/api/cars/yard-in', data);

    putInnerCargo = (id: number, items: InnerCargo[]) => this.base.axios
        .put(`/api/inner-cargo/${id}`, {items});

    changeShipper = (id: number, companyId: number) => this.base.axios
        .put(`/api/cars/${id}/shipper`, {companyId});

    setShipDateLimit = (id: number, after: boolean, date: string | null) => this.base.axios
        .put(`/api/cars/${id}/ship-date-limit`, {after, date});

    setNotes = (id: number, notes: { [name: string]: string }) => this.base.axios
        .put(`/api/cars/${id}/notes`, notes);

    downloadList = (params: any) => this.base.axios
        .get('/api/cars/list/download', {
            params,
            paramsSerializer: createQueryString,
            responseType: 'blob',
        });

    expCertArrived = (id: number, date: string) => this.base.axios
        .put(`/api/cars/${id}/exp-cert-arrived`, {date});
}
